import { reportingTransactions } from "./allocations";
import { isSpendable } from "./finance";
import type { Budget, BudgetOwner, Transaction } from "../types";

/** Sentinel category meaning "the owner's whole spend," not a real category option. */
export const OVERALL_CATEGORY = "__overall__";

/** The budget in effect for one owner+category in a given month: the most recent row at or
 * before that month whose Effective Month exactly matches, or which repeats going forward. */
export function resolveBudget(
  budgets: Budget[],
  owner: BudgetOwner,
  category: string,
  month: string,
): Budget | undefined {
  return budgets
    .filter(
      (b) =>
        b.owner === owner &&
        b.category === category &&
        b.month <= month &&
        (b.month === month || b.repeats),
    )
    .sort((a, b) => a.month.localeCompare(b.month))
    .at(-1);
}

/** Every owner+category pair that has any budget history at all, deduped. */
export function budgetedPairs(
  budgets: Budget[],
): { owner: BudgetOwner; category: string }[] {
  const seen = new Set<string>();
  const pairs: { owner: BudgetOwner; category: string }[] = [];
  for (const b of budgets) {
    const key = `${b.owner}:${b.category}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pairs.push({ owner: b.owner, category: b.category });
  }
  return pairs;
}

/** Spend for one owner+category in a month, in the budget's currency's base terms
 * (reportable amount, base currency, Expense rows only). "Household" combines both owners. */
export function categorySpend(
  transactions: Transaction[],
  owner: BudgetOwner,
  category: string,
  month: string,
): number {
  return reportingTransactions(transactions)
    .filter(
      (t) =>
        isSpendable(t) &&
        (category === OVERALL_CATEGORY || t.category === category) &&
        t.date.slice(0, 7) === month &&
        (owner === "Household" || t.owner === owner) &&
        t.reportableAmountBase !== null,
    )
    .reduce((sum, t) => sum + (t.reportableAmountBase ?? 0), 0);
}
